/* Read-only presentation adapter. CartBot is the sole automatic cart writer.
 * A zero-priced, correctly discounted line is evidence of the current cart only;
 * neither this file nor a theme setting grants discount/checkout eligibility. */
(() => {
  const inspectCart = (cart, config) => {
    if (!cart || !Array.isArray(cart.items)) {
      return { state: 'unverified', hasGift: false, quantity: 0 };
    }
    const gifts = cart.items.filter((line) => String(line.variant_id ?? line.id) === config.variantId);
    const quantity = gifts.reduce((sum, line) => sum + line.quantity, 0);
    if (cart.currency !== config.currency) return { state: 'unverified', hasGift: gifts.length > 0, quantity };
    if (!gifts.length) return { state: 'absent', hasGift: false, quantity: 0 };
    if (!config.giftReady) return { state: 'unavailable', hasGift: true, quantity };
    if (quantity !== 1 || gifts.length !== 1) return { state: 'duplicate', hasGift: true, quantity };
    const line = gifts[0];
    const hasDiscount = line.line_level_discount_allocations?.some(
      (allocation) => allocation.amount > 0 && allocation.discount_application?.title === config.discountTitle
    );
    return {
      state: line.final_line_price === 0 && hasDiscount ? 'confirmed' : 'pending',
      hasGift: true,
      quantity,
    };
  };

  const evaluateCheckout = (cart, config) => {
    const gift = inspectCart(cart, config);
    const block = (reason) => ({ blocked: true, reason });
    if (gift.hasGift && gift.state !== 'confirmed') return block(gift.state);
    if (!config.giftReady) return { blocked: false };
    if (gift.state === 'unverified') return block('unverified');
    if (!cart.items.length) return { blocked: false, nonGiftNet: 0 };

    // Shopify cart.total_price already includes BOTH line and cart discounts.
    // Do not subtract total_discount or line-level allocations a second time.
    // When the gift is absent or its line is already zero, its contribution is 0:
    // total_price is precisely the non-gift merchandise net. A paid gift was
    // blocked above; do not guess its share of cart-level discount allocation.
    // Official field semantics: shopify.dev/docs/api/liquid/objects/cart#total_price
    // final_line_price includes only line discounts, not cart-level discounts.
    if (config.taxesIncluded !== false || config.dutiesIncluded !== false ||
        !Number.isSafeInteger(cart.total_price) || cart.total_price < 0) return block('net_unverified');
    const nonGiftNet = cart.total_price;
    if (nonGiftNet >= config.threshold && !gift.hasGift) return block('missing');
    if (nonGiftNet < config.threshold && gift.hasGift) return block('ineligible');
    return { blocked: false, nonGiftNet };
  };

  // Also reject malformed/stale response data rather than exposing a promotional claim.
  const validConfig = (config) =>
    config &&
    typeof config.shippingReady === 'boolean' &&
    typeof config.giftReady === 'boolean' &&
    typeof config.giftEnabled === 'boolean' &&
    /^[A-Z]{3}$/.test(config.currency) &&
    /^[A-Z]{2}$/.test(config.country) &&
    (!config.giftReady ||
      (config.giftEnabled &&
        /^[1-9]\d*$/.test(config.variantId) &&
        typeof config.discountTitle === 'string' &&
        config.discountTitle.trim().length > 0 &&
        Number.isSafeInteger(config.threshold) &&
        config.threshold > 0 &&
        typeof config.thresholdLabel === 'string' &&
        config.thresholdLabel.length > 0 &&
        (config.country !== 'US' || config.currency !== 'USD' || config.threshold === 3000)));

  // No build step or dependency; allow the same safety logic to run in local Node tests.
  if (typeof module !== 'undefined' && module.exports) module.exports = { inspectCart, validConfig, evaluateCheckout };
  if (typeof document === 'undefined') return;

  const configNode = document.querySelector('[data-s01-offer-config]');
  if (!configNode) return;
  let config;
  try {
    config = JSON.parse(configNode.textContent);
    if (!validConfig(config)) return;
  } catch {
    return;
  }
  if (!config.giftEnabled && !config.shippingReady) return;

  const root = window.Shopify?.routes?.root || '/';
  const messages = {
    confirmed: 'Your bag contains one gift priced at 0. Confirm it remains free at checkout.',
    duplicate: 'There is more than one gift in your bag. Remove the extra gift before checkout.',
    pending: 'Your gift is not free yet. Update your bag or remove the gift before checkout.',
    unavailable: 'Gift availability has changed. Remove the gift before checkout.',
    unverified: 'We could not refresh the gift status. Refresh your bag before checkout.',
    net_unverified: 'We could not confirm the merchandise total excluding tax and duties. Please refresh your bag before checkout.',
    missing: 'Your gift is being added. Please wait before checkout.',
    ineligible: 'Your total is below the gift threshold. Please wait for the gift to be removed before checkout.',
  };
  let snapshot = { state: 'unverified', hasGift: false, quantity: 0 };
  let checkoutDecision = { blocked: true, reason: 'unverified' };
  let checkoutMessage = '';
  let checkoutWaiting = false;
  let replay = null;
  let verified = false;
  let timer;
  let refreshPromise = null;
  let queued = false;

  const render = () => {
    const attention = checkoutDecision.blocked;
    document.querySelectorAll('[data-s01-offer]').forEach((notice) => {
      const shipping = notice.querySelector('[data-s01-shipping]');
      const gift = notice.querySelector('[data-s01-gift]');
      const threshold = notice.querySelector('[data-s01-threshold]');
      const status = notice.querySelector('[data-s01-gift-status]');
      shipping.hidden = !verified || !config.shippingReady;
      gift.hidden = !verified || !config.giftReady || attention;
      if (threshold.textContent !== config.thresholdLabel) threshold.textContent = config.thresholdLabel;
      if (status) {
        const message = checkoutMessage || (config.giftEnabled || snapshot.hasGift
          ? messages[checkoutDecision.reason] || messages[snapshot.state] || '' : '');
        status.hidden = !message;
        status.dataset.state = attention ? 'attention' : snapshot.state;
        if (status.textContent !== message) status.textContent = message;
      }
      notice.hidden = shipping.hidden && gift.hidden && (!status || status.hidden);
    });
    document.querySelectorAll('[data-s01-announcement-fallback]').forEach((notice) => {
      notice.hidden = verified && (config.shippingReady || (config.giftReady && !attention));
    });

    // UI restraint only. Keep removal usable; do not write a cart or clamp saved lines.
    // The backend/app must still prevent extra gifts and limit the discount to one.
    if (config.giftEnabled && config.variantId) {
      document.querySelectorAll('input[data-quantity-variant-id]').forEach((input) => {
        if (String(input.dataset.quantityVariantId) !== config.variantId) return;
        input.max = '1';
        const increase = input.closest('quantity-input')?.querySelector('button[name="plus"]');
        if (increase && Number(input.value) >= 1) increase.disabled = true;
      });
    }
  };

  const get = async (url, signal) => {
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal });
    if (!response.ok) throw new Error('Offer status unavailable');
    return response.json();
  };

  const refresh = () => {
    if (refreshPromise) {
      queued = true;
      return refreshPromise;
    }
    refreshPromise = (async () => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 4500);
    try {
      // Rerender with Shopify's actual country/currency and fresh tracked gift inventory.
      // Never infer checkout currency from Nova's display label or convert USD in JS.
      const [sections, cart] = await Promise.all([
        get(`${root}?sections=s01-offer-status`, controller.signal),
        get(`${root}cart.js`, controller.signal),
      ]);
      const html = new DOMParser().parseFromString(sections['s01-offer-status'] || '', 'text/html');
      const next = JSON.parse(html.querySelector('[data-s01-offer-config]')?.textContent || 'null');
      if (!validConfig(next) || next.country !== config.country || next.currency !== config.currency) {
        throw new Error('Offer market changed; reload required');
      }
      config = next;
      snapshot = inspectCart(cart, config);
      checkoutDecision = evaluateCheckout(cart, config);
      verified = snapshot.state !== 'unverified';
    } catch {
      verified = false;
      snapshot = { ...snapshot, state: 'unverified' };
      checkoutDecision = { blocked: true, reason: 'unverified' };
    } finally {
      clearTimeout(timeout);
      render();
    }
    })().finally(() => {
      refreshPromise = null;
      if (queued) {
        queued = false;
        schedule();
      }
    });
    return refreshPromise;
  };

  const schedule = () => {
    clearTimeout(timer);
    timer = setTimeout(refresh, 250);
  };

  const focusStatus = () => {
    const notice = [...document.querySelectorAll('[data-s01-gift-status]')].find(
      (node) => !node.hidden && node.getClientRects().length > 0
    );
    if (notice) {
      notice.tabIndex = -1;
      notice.focus();
    }
  };

  const settleCheckout = async (action) => {
    checkoutWaiting = true;
    checkoutMessage = 'Checking your bag and automatic gift. Please wait.';
    render();
    focusStatus();
    try {
      // Read even if the previous snapshot was below threshold or had no gift:
      // the customer may have just added an item while CartBot is still running.
      for (let attempt = 0; attempt < 8; attempt += 1) {
        await refresh();
        if (!checkoutDecision.blocked) {
          checkoutMessage = '';
          render();
          if (action.href) {
            window.location.assign(action.href);
          } else {
            const form = document.getElementById(action.form.id) || action.form;
            const button = document.getElementById(action.button.id) || action.button;
            if (!form.isConnected || button.form !== form || button.disabled) throw new Error('Cart changed');
            replay = { form, button };
            // Preserve the real checkout submitter, constraint checks and other app hooks.
            // requestSubmit dispatches synchronously; the exemption lives for this event only.
            try { form.requestSubmit(button); } finally { replay = null; }
          }
          return;
        }
        checkoutMessage = messages[checkoutDecision.reason] || messages.unverified;
        render();
        focusStatus();
        if (!['missing', 'pending', 'ineligible'].includes(checkoutDecision.reason) || attempt === 7) break;
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
      checkoutMessage = checkoutDecision.reason === 'missing'
        ? 'Your gift has not been added yet. Please wait a moment, then try checkout again.'
        : messages[checkoutDecision.reason] || messages.unverified;
    } catch {
      checkoutMessage = messages.unverified;
    } finally {
      checkoutWaiting = false;
      render();
      focusStatus();
    }
  };

  // Fresh-read/settling guard for ordinary theme checkout. It never adds a gift,
  // grants a discount, or covers accelerated/checkout-side/backend enforcement.
  const guardCheckout = (event) => {
    if (event.type === 'submit' && replay?.form === event.target && replay?.button === event.submitter) return;
    let isCheckout = event.type === 'submit' && event.submitter?.name === 'checkout';
    let action = isCheckout ? { form: event.target, button: event.submitter } : null;
    if (event.type === 'click') {
      const target = event.target.closest?.('button[name="checkout"], input[name="checkout"], a[href]');
      isCheckout = target?.name === 'checkout';
      if (isCheckout) action = { form: target.form, button: target };
      if (target?.tagName === 'A') {
        const url = new URL(target.href, window.location.href);
        isCheckout = url.origin === window.location.origin && /\/checkout\/?$/.test(url.pathname);
        if (isCheckout) action = { href: url.href };
      }
    }
    if (!isCheckout || (!config.giftReady && !snapshot.hasGift)) return;
    event.preventDefault();
    if (!checkoutWaiting && (action.href || (action.form && action.button))) void settleCheckout(action);
  };
  document.addEventListener('click', guardCheckout, true);
  document.addEventListener('submit', guardCheckout, true);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, schedule);
  }
  document.addEventListener('cart:lines-update', schedule);
  document.addEventListener('shopify:section:load', schedule);
  window.addEventListener('pageshow', schedule);
  window.addEventListener('focus', schedule);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) schedule();
  });

  // CartBot may use fetch or XHR without Dawn events. Observe completed cart writes
  // without replacing either network API or relying on an undocumented app event.
  if (typeof PerformanceObserver !== 'undefined') {
    try {
      const observer = new PerformanceObserver((list) => {
        if (list.getEntries().some((entry) => {
          const url = new URL(entry.name, window.location.href);
          return url.origin === window.location.origin && /\/cart\/(add|change|update|clear)(\.(js|json))?\/?$/.test(url.pathname);
        })) schedule();
      });
      observer.observe({ type: 'resource', buffered: false });
    } catch {
      // Older browsers still refresh through Dawn events, focus and the stock timer.
    }
  }
  new MutationObserver((records) => {
    const hasNewCart = records.some((record) => [...record.addedNodes].some((node) =>
      node.nodeType === 1 && (node.matches('[data-s01-offer], cart-drawer-items, cart-items') ||
        node.querySelector('[data-s01-offer]'))
    ));
    if (hasNewCart) {
      render();
      schedule();
    }
  }).observe(document.body, { childList: true, subtree: true });
  setInterval(() => {
    if (!document.hidden) schedule();
  }, 60000);
  refresh();
})();
